import type { TFunction } from 'i18next';

import { partitionArray, sort, sortAsModel } from 'app/utils/array';
import type { BracketLiaison } from 'app/components/Blocks/BracketsBlock/types';
import { getMatchOpponents } from 'app/components/Blocks/BracketsBlock/utils';
import { isDefined } from 'app/utils/types';

// Tag of the invisible matches filling the missing slots of a seed (see getSeedWithPlaceholders).
const PLACEHOLDER_MATCH_TAG = '__placeholder__';

const isPlaceholderMatch = (match: TournamentBracketMatch): boolean =>
  match.tag === PLACEHOLDER_MATCH_TAG;

const getPredecessorsLength = (
  columnIndex: number,
  seedIndex: number,
  orderedColumns: TournamentBracketColumn[]
): number => {
  for (let i = columnIndex - 1; i >= 0; i -= 1) {
    const nbMatches = orderedColumns[i]?.seeds[seedIndex]?.matches.length || 0;
    if (nbMatches > 0) {
      return Math.round(nbMatches / 2);
    }
  }
  return 0;
};

/* ****** Liaisons ****** */

const getMatchLiaisonWithSeed = (
  curSeed: TournamentBracketSeed,
  matchIndex: number,
  comparedSeed: TournamentBracketSeed | undefined
): BracketLiaison | undefined => {
  let liaison: BracketLiaison | undefined;

  if (comparedSeed) {
    if (comparedSeed.matches.length > curSeed.matches.length) {
      liaison = 'straight';
    } else if (comparedSeed.matches.length === curSeed.matches.length) {
      if (curSeed.position === 'lower') {
        liaison = 'top-straight';
      } else {
        liaison = 'straight';
      }
    } else if (
      // if one seed has an odd number of matches and the other has an even number of matches, we don't show a liaison to not screw up the display
      // exception: for seeds with 2 matches that merge into one, or when curSeed has exactly 1 more match than comparedSeed
      Math.abs(curSeed.matches.length - comparedSeed.matches.length) % 2 === 1 &&
      curSeed.matches.length + comparedSeed.matches.length > 3
    ) {
      if (curSeed.matches.length - comparedSeed.matches.length === 1) {
        // One extra unpaired match (e.g. 3→2). Initial bottom/top pairing; the solo match is then
        // corrected to 'top-straight' by the previous_matches override in getMatchLiaisons.
        liaison = matchIndex % 2 === 0 ? 'bottom' : 'top';
      } else {
        liaison = undefined;
      }
    } else {
      liaison = matchIndex % 2 === 0 ? 'bottom' : 'top';
    }
  }

  return liaison;
};

const getMatchLiaisonWithColumn = (
  cols: TournamentBracketColumn[],
  match: TournamentBracketMatch,
  matchIndex: number
): BracketLiaison | undefined => {
  let liaison: BracketLiaison | undefined;

  const [curCol, ...otherCols] = cols;
  if (curCol) {
    const curSeedIndex = curCol.seeds.findIndex(seed =>
      seed.matches.map(({ id }) => id).includes(match.id)
    );
    const curSeed = curCol.seeds.at(curSeedIndex);
    if (curSeedIndex > -1 && curSeed) {
      const comparedCol = otherCols.find(col => {
        const seedAtIndex = col.seeds.at(curSeedIndex);
        if (seedAtIndex && seedAtIndex.matches.length) {
          return true;
        }
        return false;
      });
      if (comparedCol) {
        if (comparedCol.seeds.length === curCol.seeds.length) {
          const comparedSeed = comparedCol.seeds.at(curSeedIndex);
          if (comparedSeed) {
            liaison = getMatchLiaisonWithSeed(curSeed, matchIndex, comparedSeed);
          }
        }
      }
    }
  }
  return liaison;
};

const getMatchLiaisons = (
  curColIndexRelativetToFirstColumnWithSeeds: number,
  cols: TournamentBracketColumn[],
  colIndex: number,
  match: TournamentBracketMatch,
  matchIndex: number
): [BracketLiaison | undefined, BracketLiaison | undefined] => {
  if (curColIndexRelativetToFirstColumnWithSeeds < 0) {
    return [undefined, undefined];
  }

  const nextCols = cols.slice(colIndex);

  let nextLiasion = getMatchLiaisonWithColumn(nextCols, match, matchIndex);

  // Override nextLiaison for the N+1→N case (e.g. 3→2) using previous_matches, because
  // the solo match's position in the seed changes as the tournament progresses.
  if (nextLiasion !== undefined && colIndex + 1 < cols.length) {
    const curCol = cols[colIndex];
    const nextCol = cols[colIndex + 1];
    if (curCol && nextCol) {
      const curSeedIndex = curCol.seeds.findIndex(seed =>
        seed.matches.some(m => m.id === match.id)
      );
      if (curSeedIndex > -1) {
        const curSeed = curCol.seeds.at(curSeedIndex);
        const nextSeed = nextCol.seeds.at(curSeedIndex);
        if (curSeed && nextSeed && curSeed.matches.length - nextSeed.matches.length === 1) {
          const curMatchIds = new Set(curSeed.matches.map(m => m.id));
          const fedNextMatch = nextSeed.matches.find(nm =>
            nm.previous_matches.some(pm => pm.type === 'winner' && pm.match_id === match.id)
          );
          if (fedNextMatch) {
            const winnersFromCurSeed = fedNextMatch.previous_matches.filter(
              pm => pm.type === 'winner' && curMatchIds.has(pm.match_id)
            ).length;
            if (winnersFromCurSeed === 1) {
              nextLiasion = 'top-straight';
            } else {
              const pairedMatchId = fedNextMatch.previous_matches.find(
                pm =>
                  pm.type === 'winner' && pm.match_id !== match.id && curMatchIds.has(pm.match_id)
              )?.match_id;
              const pairedMatchIndex = curSeed.matches.findIndex(m => m.id === pairedMatchId);
              nextLiasion = matchIndex < pairedMatchIndex ? 'bottom' : 'top';
            }
          }
        }
      }
    }
  }

  if (curColIndexRelativetToFirstColumnWithSeeds === 0) {
    return [undefined, nextLiasion];
  }

  const prevCols = cols.slice(0, colIndex + 1).reverse();

  let prevLiasion = getMatchLiaisonWithColumn(prevCols, match, matchIndex);

  // Override prevLiaison using previous_matches data to handle non-standard bracket connections:
  // - 0 winners from the compared seed → no liaison (e.g. LB QF match fed only by UB losers, not LBR1)
  // - 1 winner from a larger compared seed + count-based liaison is 'straight' → 'top-straight'
  //   (e.g. UB SF match with one QF predecessor and one direct seed like a bye)
  if (match.previous_matches.length > 0 && colIndex > 0) {
    const curCol = cols[colIndex];
    const prevCol = cols[colIndex - 1];
    if (curCol && prevCol) {
      const curSeedIndex = curCol.seeds.findIndex(seed =>
        seed.matches.some(m => m.id === match.id)
      );
      if (curSeedIndex > -1) {
        const curSeed = curCol.seeds.at(curSeedIndex);
        const prevSeed = prevCol.seeds.at(curSeedIndex);
        if (prevSeed && prevSeed.matches.length > 0) {
          const prevMatchIds = new Set(prevSeed.matches.map(m => m.id));
          const winnersFromPrev = match.previous_matches.filter(
            pm => pm.type === 'winner' && prevMatchIds.has(pm.match_id)
          ).length;

          if (winnersFromPrev === 0) {
            prevLiasion = undefined;
          } else if (
            prevLiasion === 'straight' &&
            winnersFromPrev === 1 &&
            curSeed &&
            prevSeed.matches.length > curSeed.matches.length &&
            // a padded seed already has a placeholder in front of the direct seed
            !prevSeed.matches.some(isPlaceholderMatch)
          ) {
            prevLiasion = 'top-straight';
          }
        }
      }
    }
  }

  return [prevLiasion, nextLiasion];
};

const getSeedLiaison = (
  curCol: TournamentBracketColumn,
  seedIndex: number,
  comparedCol: TournamentBracketColumn | undefined
): BracketLiaison | undefined => {
  let liaison: BracketLiaison | undefined;

  if (comparedCol) {
    if (comparedCol.seeds.length !== curCol.seeds.length) {
      if (comparedCol.seeds.length > curCol.seeds.length) {
        liaison = 'straight';
      } else if (seedIndex === 0) {
        liaison = 'bottom';
      } else if (seedIndex === curCol.seeds.length - 1) {
        liaison = 'top';
      } else {
        liaison = 'top-bottom';
      }
    }
  }

  return liaison;
};

const getSeedLiaisons = (
  curColIndexRelativetToFirstColumnWithSeeds: number,
  prevCol: TournamentBracketColumn | undefined,
  curCol: TournamentBracketColumn,
  seedIndex: number,
  nextCol: TournamentBracketColumn | undefined
): [BracketLiaison | undefined, BracketLiaison | undefined] => {
  if (curColIndexRelativetToFirstColumnWithSeeds < 0) {
    return [undefined, undefined];
  }

  const nextLiasion = getSeedLiaison(curCol, seedIndex, nextCol);

  if (curColIndexRelativetToFirstColumnWithSeeds === 0) {
    return [undefined, nextLiasion];
  }

  const prevLiasion = getSeedLiaison(curCol, seedIndex, prevCol);

  return [prevLiasion, nextLiasion];
};

/* ****** Seeds, matches and opponents ordering ****** */
// Here's the trick used to order matches and opponents in the brackets :
// For each column, we look at the order of all the opponents of all the column's matches.
// We get a unique ID for each opponent, based on its ID and its seed position.
// Then, we sort the matches of the previous column based on the order of the opponents.
// We sort the matches and not the opponents.
// Except for the final, since there is only one match, we sort the opponents directly.
// We start by the 2nd to last column, because for double elimination tournament, the order is enforced by the seed position (upper, lower).
// We then sort each column that way, until we reach the first column.

// Utils to generate empty seed.
const generateEmptySeed = (position: SeedPosition): TournamentBracketSeed => ({
  position,
  matches: []
});

// Get the unique ordering ID for the opponents.
const getOpponentOrderingId = (
  opponentId: number | null,
  position: SeedPosition | null
): string => {
  const possibleValue = ['upper', 'lower', null];
  return `${opponentId}_${possibleValue.includes(position) ? position : null}`;
};

// Get opponents order for the current already sorted column.
// This will serve as the model onto which we will sort the previous column.
const getOpponentsOrder = (column: TournamentBracketColumn): (string | null)[] =>
  column.seeds.flatMap(seed =>
    seed.matches.flatMap(match =>
      match.opponents.map(opponent => getOpponentOrderingId(opponent.id, seed.position))
    )
  );

// Get the final opponents order.
// This is a special case, since there is only one match in the final.
// We get the opponent order based on the 2nd to last column.
const getFinalOpponentsOrder = (
  beforeFinalSeeds: TournamentBracketSeed[]
): [string | null, string | null] => {
  if (beforeFinalSeeds.length === 2) {
    const topSeed = beforeFinalSeeds[0];
    const bottomSeed = beforeFinalSeeds[1];
    return [
      getOpponentOrderingId(topSeed.matches[0]?.winner_id, 'upper'),
      getOpponentOrderingId(bottomSeed.matches[0]?.winner_id, 'lower')
    ];
  }
  if (beforeFinalSeeds.length === 1) {
    return [
      getOpponentOrderingId(beforeFinalSeeds[0].matches[0].winner_id, null),
      getOpponentOrderingId(beforeFinalSeeds[0].matches[1]?.winner_id, null)
    ];
  }
  return [null, null];
};

// Method to order the matches inside the column, based on the ordered opponents array we pass.
// We also sort the opponents of the match if the flag sortOpponents is true.
const getOrderedColumn = (
  column: TournamentBracketColumn,
  orderedOpponents: (string | null)[],
  sortOpponents: boolean
): TournamentBracketColumn => ({
  ...column,
  seeds: column.seeds.map(seed => {
    const matchesWithSortedOpponents = sortOpponents
      ? seed.matches.map(match => ({
          ...match,
          opponents: sortAsModel(match.opponents, orderedOpponents, opponent =>
            getOpponentOrderingId(opponent.id, seed.position)
          )
        }))
      : seed.matches;
    return {
      ...seed,
      matches: sortAsModel(
        sort(
          matchesWithSortedOpponents,
          // Put the matches with 2 opponents at the bottom
          match => match.opponents.filter(o => o.slug !== 'tbd').length
        ),
        orderedOpponents,
        match => getOpponentOrderingId(match.winner_id, seed.position)
      )
    };
  })
});

const getSwissColumnsWithTBD = (columns: TournamentBracketColumn[]): TournamentBracketColumn[] =>
  columns.map((column, columnIndex) => ({
    ...column,
    seeds: column.seeds.map((seed, seedIndex) => {
      if (isDefined(seed.nb_matches)) {
        const nbOfTBDMatchs = seed.nb_matches - seed.matches.length;
        const tbdMatches: TournamentBracketMatch[] = new Array(Math.max(nbOfTBDMatchs, 0))
          .fill(null)
          .map((_, matchIndex) => ({
            id: columnIndex * 1000 + seedIndex * 100 + matchIndex,
            begin_at: '',
            forfeit: false,
            status: 'not_started',
            game_advantage: null,
            opponents: [],
            games: [],
            match_type: '',
            winner_id: null,
            tag: '',
            number_of_games: 0,
            previous_matches: []
          }));
        return {
          ...seed,
          matches: [...seed.matches, ...tbdMatches]
        };
      }
      return seed;
    })
  }));

// Order matches within a column using the previous_matches relationships from the next column.
// This gives correct ordering for unplayed tournaments where winner_id is null and the
// winner_id-based sortAsModel has no effect.
const applyPreviousMatchesOrder = (
  column: TournamentBracketColumn,
  nextColumn: TournamentBracketColumn
): TournamentBracketColumn => {
  const previousMatchesById = new Map<number, TournamentBracketMatch>();
  column.seeds.forEach(seed => {
    seed.matches.forEach(match => previousMatchesById.set(match.id, match));
  });

  const matchIdOrder = new Map<number, number>();
  let position = 0;
  nextColumn.seeds.forEach(seed => {
    seed.matches.forEach(nextMatch => {
      const winnerPreviousMatches = nextMatch.previous_matches.filter(pm => pm.type === 'winner');
      // previous_matches carries no slot information and the API gives no ordering guarantee
      // on it, so we can't rely on its array order (this is what made match order unstable).
      // When a previous match is decided, its winner_id tells us which opponent slot of
      // nextMatch it feeds into - use that instead. Falls back to the original previous_matches
      // order when the winner isn't known yet (unplayed brackets).
      const orderedWinnerMatches = sort(winnerPreviousMatches, pm => {
        const winnerId = previousMatchesById.get(pm.match_id)?.winner_id;
        const slotIndex = isDefined(winnerId)
          ? nextMatch.opponents.findIndex(opponent => opponent.id === winnerId)
          : -1;
        return slotIndex === -1 ? null : slotIndex;
      });
      orderedWinnerMatches.forEach(pm => {
        matchIdOrder.set(pm.match_id, position);
        position += 1;
      });
    });
  });

  if (matchIdOrder.size === 0) return column;

  return {
    ...column,
    seeds: column.seeds.map(seed => ({
      ...seed,
      matches: sort(seed.matches, item => matchIdOrder.get(item.id) ?? Infinity)
    }))
  };
};

/* ****** Placeholder matches ****** */
// When an opponent of a next-round match doesn't come from the current round (e.g. a bye, or an
// upper bracket loser dropping into the lower bracket), the current seed has fewer matches than the
// bracket layout expects (e.g. 7 matches feeding 4, or 3 feeding 4). We fill the missing slots with
// invisible placeholder matches so that every match is aligned with the match it feeds into.

const generatePlaceholderMatch = (id: number): TournamentBracketMatch => ({
  id,
  begin_at: '',
  forfeit: false,
  status: 'not_started',
  game_advantage: null,
  opponents: [],
  games: [],
  match_type: '',
  winner_id: null,
  tag: PLACEHOLDER_MATCH_TAG,
  number_of_games: 0,
  previous_matches: []
});

// Returns the [top, bottom] slots of nextMatch filled by the given seed matches.
// `undefined` means the slot is filled by an opponent coming from somewhere else.
const getNextMatchSlots = (
  nextMatch: TournamentBracketMatch,
  feedingMatches: TournamentBracketMatch[]
): [TournamentBracketMatch | undefined, TournamentBracketMatch | undefined] => {
  if (feedingMatches.length !== 1) {
    return [feedingMatches[0], feedingMatches[1]];
  }
  const [feedingMatch] = feedingMatches;
  // The feeding match winner is known: its slot is the one it fills in nextMatch.
  const winnerSlot = isDefined(feedingMatch.winner_id)
    ? nextMatch.opponents.findIndex(opponent => opponent.id === feedingMatch.winner_id)
    : -1;
  // Otherwise, a known opponent in nextMatch is the one coming from somewhere else.
  const directOpponentSlot = nextMatch.opponents.findIndex(opponent => opponent.slug !== 'tbd');
  const isFeedingTopSlot = winnerSlot > -1 ? winnerSlot === 0 : directOpponentSlot !== 0;
  return isFeedingTopSlot ? [feedingMatch, undefined] : [undefined, feedingMatch];
};

const getSeedWithPlaceholders = (
  seed: TournamentBracketSeed,
  nextSeed: TournamentBracketSeed,
  placeholderIdBase: number
): TournamentBracketSeed => {
  const nbMatches = seed.matches.length;
  const nbNextMatches = nextSeed.matches.length;
  // 1 match per next match (e.g. lower bracket rounds where upper bracket losers drop in)
  // or 2 matches per next match (regular elimination round).
  if (nbMatches === 0 || nbMatches === nbNextMatches || nbMatches >= 2 * nbNextMatches) {
    return seed;
  }
  const slotsPerNextMatch = nbMatches < nbNextMatches ? 1 : 2;

  // For each next match, the matches of the seed feeding it, in the current seed order
  // (already set by the previous_matches / winner_id ordering).
  const feedingMatchesByNextMatch = nextSeed.matches.map(nextMatch => {
    const feedingMatchIds = nextMatch.previous_matches
      .filter(pm => pm.type === 'winner')
      .map(pm => pm.match_id);
    return seed.matches.filter(match => feedingMatchIds.includes(match.id));
  });

  // Each match must feed exactly one next match, within the available slots.
  // Otherwise we can't safely place them, so we keep the seed as is.
  const feedingMatchIds = feedingMatchesByNextMatch.flat().map(match => match.id);
  if (
    feedingMatchesByNextMatch.some(feedingMatches => feedingMatches.length > slotsPerNextMatch) ||
    feedingMatchIds.length !== nbMatches ||
    new Set(feedingMatchIds).size !== nbMatches
  ) {
    return seed;
  }

  const slots = feedingMatchesByNextMatch.flatMap((feedingMatches, nextMatchIndex) =>
    slotsPerNextMatch === 1
      ? [feedingMatches[0]]
      : getNextMatchSlots(nextSeed.matches[nextMatchIndex], feedingMatches)
  );

  return {
    ...seed,
    matches: slots.map(
      (match, slotIndex) => match ?? generatePlaceholderMatch(placeholderIdBase - slotIndex - 1)
    )
  };
};

const getColumnWithPlaceholders = (
  column: TournamentBracketColumn,
  nextColumns: TournamentBracketColumn[],
  columnIndex: number
): TournamentBracketColumn => ({
  ...column,
  seeds: column.seeds.map((seed, seedIndex) => {
    const nextSeed = nextColumns
      .filter(nextColumn => nextColumn.seeds.length === column.seeds.length)
      .map(nextColumn => nextColumn.seeds[seedIndex])
      .find(nextColumnSeed => nextColumnSeed && nextColumnSeed.matches.length > 0);
    // Negative ids so they can't collide with real matches.
    return nextSeed
      ? getSeedWithPlaceholders(seed, nextSeed, -(columnIndex * 10000 + seedIndex * 1000))
      : seed;
  })
});

// Main method to get the ordered columns.
const getOrderedColumns = (
  columns: TournamentBracketColumn[],
  tournamentType: TournamentType,
  tournamentId: number
): [TournamentBracketColumn[], TournamentBracketSeed | undefined] => {
  const columnsWithTBD =
    tournamentType === 'swiss_stage' ? getSwissColumnsWithTBD(columns) : columns;

  // First we make sure that each match has 2 opponents.
  const columnsWith2opponentsMatchs: TournamentBracketColumn[] = columnsWithTBD.map(column => ({
    ...column,
    seeds: column.seeds.map(seed => ({
      ...seed,
      matches: seed.matches.map(match => ({
        ...match,
        opponents: getMatchOpponents(match)
      }))
    }))
  }));

  // Then, to properly display the seeds, we need to match the upper seeds to the lower seeds.
  // We first get all the upper seeds.
  let allUpperSeeds = columnsWith2opponentsMatchs.flatMap(column =>
    column.seeds.filter(seed => seed.position === 'upper')
  );

  // We iterate over the columns
  const columnsWithMatchingSeeds = columnsWith2opponentsMatchs.reduce((newColumns, column) => {
    // We split the array to get the lower seed and the other seeds (excluding the upper seeds).
    const [[lowerSeed], otherSeeds] = partitionArray(column.seeds, seed =>
      seed.position === 'lower' ? 0 : 1
    );
    const [allOtherSeeds, upperSeedsInColumn] = partitionArray(otherSeeds, seed =>
      seed.position !== 'upper' ? 0 : 1
    );

    // This will hold the upper seed that has all the previous matches of the lower seed we analyse.
    let upperSeedWithPreviousMatches: TournamentBracketSeed | undefined;

    if (lowerSeed) {
      // Get all the previous matches of the lower seed, when these matches are of type 'loser' (coming from the upper seed).
      const allPreviousMatches = lowerSeed.matches.flatMap(match =>
        match.previous_matches
          .filter(({ type }) => type === 'loser')
          .map(({ match_id }) => match_id)
      );

      // Find the upper seed that has all the previous matches of the lower seed.
      const upperSeedWithPreviousMatchesIndex = allUpperSeeds.findIndex(seed =>
        seed.matches.some(match => allPreviousMatches.includes(match.id))
      );

      if (upperSeedWithPreviousMatchesIndex > -1) {
        upperSeedWithPreviousMatches = allUpperSeeds[upperSeedWithPreviousMatchesIndex];

        // If we found the upper seed, we remove it from the array containing all upper seeds.
        allUpperSeeds = allUpperSeeds.filter(
          (_, index) => index !== upperSeedWithPreviousMatchesIndex
        );
      }

      // We add all the seeds to the column
      const seeds = upperSeedWithPreviousMatches
        ? [...allOtherSeeds, lowerSeed, upperSeedWithPreviousMatches]
        : [...allOtherSeeds, lowerSeed];

      return [
        ...newColumns,
        {
          ...column,
          seeds
        }
      ];
    }

    const upperSeedsTitlesInColumn = upperSeedsInColumn.map(s => s.title);
    allUpperSeeds = allUpperSeeds.filter(
      upperSeed => !upperSeedsTitlesInColumn.includes(upperSeed.title)
    );

    // If there is no lower seed, we just add the column to the array.
    return [...newColumns, column];
  }, [] as TournamentBracketColumn[]);

  // If there are upper seeds left (this can happen if there are links missing in the tournament bracket form)
  // we try to match them according to the previous matches of the other seeds, starting from the last column.
  if (allUpperSeeds.length > 0) {
    allUpperSeeds.forEach(upperSeed => {
      const upperSeedMatchesIds = upperSeed.matches.map(match => match.id);
      let nextSeedColumnIndex = null;
      for (let i = columnsWithMatchingSeeds.length - 1; i >= 0; i -= 1) {
        const seed = columnsWithMatchingSeeds[i].seeds.find(seed =>
          seed.matches.some(match =>
            match.previous_matches.some(previousMatch =>
              upperSeedMatchesIds.includes(previousMatch.match_id)
            )
          )
        );
        // if the seed containing the next matches is lower, we add the upper seed to the same column
        if (seed) {
          if (seed.position === 'lower') {
            nextSeedColumnIndex = i;
            break;
          } else {
            // if the seed containing the next matches is upper, we add the upper seed to the previous column
            nextSeedColumnIndex = i - 1;
            break;
          }
        } else {
          // if there is no seed matching this, we try to position it in the column according to its own previous matches
          const upperSeedPreviousMatches = upperSeed.matches.flatMap(match =>
            match.previous_matches.map(previousMatch => previousMatch.match_id)
          );
          const previousSeed = columnsWithMatchingSeeds[i].seeds.find(seed =>
            seed.matches.some(match => upperSeedPreviousMatches.includes(match.id))
          );
          // if the previous seed is upper, we add the upper seed to the next column
          if (previousSeed && previousSeed.position === 'upper') {
            nextSeedColumnIndex = Math.min(Math.max(i + 1, 0), columnsWithMatchingSeeds.length - 1);
            break;
          }
        }
      }

      if (nextSeedColumnIndex !== null && nextSeedColumnIndex > 0) {
        columnsWithMatchingSeeds[nextSeedColumnIndex] = {
          ...columnsWithMatchingSeeds[nextSeedColumnIndex],
          seeds: [...columnsWithMatchingSeeds[nextSeedColumnIndex].seeds, upperSeed]
        };
      } else {
        // if there is no next seed column, we add the upper seed as the first column
        columnsWithMatchingSeeds.unshift({
          seeds: [upperSeed]
        });
      }
    });
  }

  // We add empty placeholders if there is only one seed in the column and the seed has an upper or lower position.
  const columnsWithEmptyPlaceholders = columnsWithMatchingSeeds.map(column => {
    if (column.seeds.length === 1) {
      if (column.seeds.at(0)?.position === 'lower') {
        return {
          ...column,
          seeds: [generateEmptySeed('upper'), column.seeds[0]]
        };
      }
      if (column.seeds.at(0)?.position === 'upper') {
        return {
          ...column,
          seeds: [column.seeds[0], generateEmptySeed('lower')]
        };
      }
    }

    return column;
  });

  // Then we filter out the third place seed.
  const columnsWithoutThirdPlaceSeed = columnsWithEmptyPlaceholders
    .map(column => ({
      ...column,
      seeds: column.seeds.filter(seed => seed.position !== 'third_place')
    }))
    .filter(column => column.seeds.length > 0);

  // And we get the third place seed independently, because it has an absolute position in the bracket display.
  const thirdPlaceSeed = columnsWithEmptyPlaceholders
    .flatMap(column => column.seeds)
    .find(seed => seed.position === 'third_place');

  // We sort the seeds of the column, based on their position.
  const columnsWithOrderedSeeds = columnsWithoutThirdPlaceSeed.map(column => ({
    ...column,
    seeds: sortAsModel(column.seeds, ['upper', 'lower'], seed => seed.position)
  }));

  // There is where the magic happens.
  let columnsWithOrderedMatches: TournamentBracketColumn[] = [];
  let lastOrderedColumn: TournamentBracketColumn | undefined;

  // We start by the final column. When we start, the lastOrderedColumn is undefined.
  // So we first determine the order of the final opponents, based on the 2nd to last column, because for double elimination tournament, the order is enforced by the seed position (upper, lower).
  // Then we order the column (with the getOrderedColumn method), store it in lastOrderedColumn, and push it into the array we will return.
  // The !lastOrderedColumn is a flag to tell the method if we need to sort the opponents of the matches only for the final.
  // Then we iterate over the columns backwards. Each time, we get the opponent order based on the lastOrderedColumn, and we sort the current column based on this order.
  for (let i = columnsWithOrderedSeeds.length - 1; i >= 0; i -= 1) {
    const column = columnsWithOrderedSeeds.at(i) || { seeds: [] };
    const orderedTeams = lastOrderedColumn
      ? getOpponentsOrder(lastOrderedColumn)
      : getFinalOpponentsOrder(columnsWithOrderedSeeds.at(-2)?.seeds || []);
    const orderedColumn = getOrderedColumn(column, orderedTeams, !lastOrderedColumn);
    // Apply previous_matches order AFTER winner_id-based sorting.
    // sortAsModel places null-winner matches at index -1 (front), which corrupts
    // partially-played brackets. previous_matches is the authoritative structural
    // order and corrects this for unplayed, partially-played, and fully-played tournaments.
    lastOrderedColumn = lastOrderedColumn
      ? applyPreviousMatchesOrder(orderedColumn, lastOrderedColumn)
      : orderedColumn;
    columnsWithOrderedMatches = [lastOrderedColumn, ...columnsWithOrderedMatches];
  }

  // Fill the missing slots of the seeds with placeholders, now that every column is ordered.
  // We go backwards, because a seed is aligned on the (possibly padded) seed of the next column.
  if (tournamentType !== 'swiss_stage' && tournamentType !== 'reseeding') {
    for (let i = columnsWithOrderedMatches.length - 2; i >= 0; i -= 1) {
      columnsWithOrderedMatches[i] = getColumnWithPlaceholders(
        columnsWithOrderedMatches[i],
        columnsWithOrderedMatches.slice(i + 1),
        i
      );
    }
  }

  // Handle TI 2025. See getSeedTitle for another adaptation
  // We remove the last 2 columns because TI has a weird swiss format where it's 4 wins to qualify and 4 wins to get eliminated
  // But all the other teams will go into another tournament, so the swiss stage stops at the 5th column
  if (tournamentId === 17283) {
    return [columnsWithOrderedMatches.slice(0, -2), thirdPlaceSeed];
  }

  return [columnsWithOrderedMatches, thirdPlaceSeed];
};

const getSwissStageRoundTitlePosition = (wins: number, losses: number): 'high' | 'mid' | 'low' => {
  if (wins > losses) {
    return 'high';
  }
  if (wins < losses) {
    return 'low';
  }
  return 'mid';
};

const getSeedTitle = (
  t: TFunction,
  seed: TournamentBracketSeed | undefined,
  nbOfSeedsInColumn: number,
  tournamentType: TournamentType,
  tournamentId: number
): string => {
  if (seed?.title) {
    if (tournamentType === 'swiss_stage') {
      const [_, roundNumber, __, wins, losses] = seed.title.split('_');

      // Handle TI 2025. See getOrderedColumns for another adaptation
      const isUsingPosition = nbOfSeedsInColumn <= 3 && tournamentId !== 17283;
      if (isUsingPosition) {
        if (nbOfSeedsInColumn === 1) {
          return `${t('blocks_bracket_seed_title_round')} ${roundNumber}`;
        }
        const winsNumber = parseInt(wins);
        const lossesNumber = parseInt(losses);
        const position = getSwissStageRoundTitlePosition(winsNumber, lossesNumber);
        if (position) {
          return `${t('blocks_bracket_seed_title_round')} ${roundNumber} ${t(
            `blocks_bracket_seed_title_round-${position}`
          )}`;
        }
      }

      return `${t('blocks_bracket_seed_title_round')} ${roundNumber} : ${wins}-${losses}`;
    }

    return t(`blocks_bracket_seed_title_${seed.title}`, {
      defaultValue: seed.title.replaceAll('__', ' : ').replaceAll('_', ' ')
    });
  }
  return '';
};

export {
  getPredecessorsLength,
  getMatchLiaisons,
  getSeedLiaisons,
  getOrderedColumns,
  getSeedTitle,
  isPlaceholderMatch
};
